import { FeatureCollection, GeoJsonProperties, Geometry, Feature } from 'geojson';
import turfArea from '@turf/area';
import turfCentroid from '@turf/centroid';
import buffer from '@turf/buffer';
import { ParsedFeatureData } from './map-types';
import { convertArea, round, point } from '@turf/helpers';
/**
 * MapboxFeature class to handle GeoJSON features and utility functions.
 * This class provides methods to parse, add, and manipulate GeoJSON features.
 * It also includes utility functions for calculating midpoints and parsing points.
 * @class MapboxFeature
 */

/**
 * @typedef {Object} MapboxFeature
 * @property {Array<Feature>} features - Array of GeoJSON features.
 * @property {Function} add - Method to dynamically add a feature based on a string input.
 * @property {Function} toJSON - Method to convert the features to a GeoJSON FeatureCollection.
 * @property {Function} parseFeatureFromString - Static method to parse a feature from a string input.
 * @property {Function} findMidPoint - Static method to calculate the centroid of a polygon.
 * @property {Function} parsePointToArray - Static method to convert a POINT string to an array.
 *
 * @example
 * // Create a new MapboxFeature instance
 * const mapboxFeature = new MapboxFeature();
 * 
 * // Add a Point feature
 * mapboxFeature.add('POINT (1 2)', { name: 'Point' });
 * 
 * // Add a Polygon feature
 * mapboxFeature.add('POLYGON ((1 1, 2 2, 3 3, 1 1))', { name: 'Polygon' });
 * 
 * // Convert features to GeoJSON
 * const geoJson = mapboxFeature.toJSON();
 * console.log(geoJson);
 * // Output: { type: 'FeatureCollection', features: [...] }
 * 
 * // Calculate the midpoint of a polygon
 * const midpoint = MapboxFeature.findMidPoint('POLYGON ((1 1, 2 2, 3 3, 1 1))');
 * console.log(midpoint);
 * // Output: POINT (2 2)
 * 
 * // Parse a POINT string to an array
 * const pointArray = MapboxFeature.parsePointToArray('POINT (1 2)');
 * console.log(pointArray);
 * // Output: [1, 2]
 */
class MapboxFeature {
  private features: Feature[];

  constructor() {
    this.features = [];
  }

  add(data: string, properties?: GeoJsonProperties): this {
    const feature = MapboxFeature.parseFeatureFromString(data, properties);
    this.features.push(feature);
    return this;
  }

  toJSON(): FeatureCollection<Geometry> {
    return {
      type: 'FeatureCollection',
      features: this.features,
    };
  }

  /**
   * Static utility function to parse and add a feature from a string input.
   * This function can be used both for adding features and calculating midpoints.
   * @param {string} data - The string representation of the feature (e.g., 'POLYGON ((x y, x y, ...))').
   * @param {GeoJsonProperties} [properties] - Optional properties for the feature.
   * @returns {Feature} - The parsed GeoJSON feature.
   */
  static parseFeatureFromString(data: string, properties?: GeoJsonProperties): Feature {
    const [type, coordinatesString] = data.split(/\s+\(/);
    const coordinates = coordinatesString
      .replace(/[()]/g, '')
      .split(',')
      .map((pair) => pair.trim().split(' ').map(Number));

    switch (type.trim().toUpperCase()) {
      case 'POINT':
        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: coordinates[0] as [number, number],
          },
          properties: {
            ...properties,
          },
        };
      case 'MULTIPOINT':
        return {
          type: 'Feature',
          geometry: {
            type: 'MultiPoint',
            coordinates: coordinates as [number, number][],
          },
          properties: {
            ...properties,
          },
        };
      case 'LINESTRING':
        return {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: coordinates as [number, number][],
          },
          properties: {
            ...properties,
          },
        };
      case 'MULTILINESTRING':
        return {
          type: 'Feature',
          geometry: {
            type: 'MultiLineString',
            coordinates: [coordinates] as [number, number][][],
          },
          properties: {
            ...properties,
          },
        };
      case 'POLYGON':
        return {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [coordinates] as [number, number][][],
          },
          properties: {
            ...properties,
          },
        };
      case 'MULTIPOLYGON':
        return {
          type: 'Feature',
          geometry: {
            type: 'MultiPolygon',
            coordinates: [[coordinates]] as [number, number][][][],
          },
          properties: {
            ...properties,
          },
        };
      default:
        throw new Error(`Unsupported geometry type: ${type}`);
    }
  }

  /**
   * 
   * @param data 
   * @returns 
   */
  static parseStringFromFeature = (data: FeatureCollection | null): string | null => {
    if (!data || !data.features || data.features.length === 0) {
      return null;
    }

    const toWKT = (geometry: Geometry): string | null => {
      const coordsToStr = (coords: number[]): string => `${coords[0]} ${coords[1]}`;

      switch (geometry.type) {
        case 'Point':
          return `POINT(${coordsToStr(geometry.coordinates)})`;

        case 'MultiPoint':
          return `MULTIPOINT(${geometry.coordinates.map(coordsToStr).join(', ')})`;

        case 'LineString':
          return `LINESTRING(${geometry.coordinates.map(coordsToStr).join(', ')})`;

        case 'MultiLineString':
          return `MULTILINESTRING(${geometry.coordinates
            .map(line => `(${line.map(coordsToStr).join(', ')})`)
            .join(', ')})`;

        case 'Polygon':
          return `POLYGON(${geometry.coordinates
            .map(ring => `(${ring.map(coordsToStr).join(', ')})`)
            .join(', ')})`;

        case 'MultiPolygon':
          return `MULTIPOLYGON(${geometry.coordinates
            .map(polygon => `(${polygon.map(ring => `(${ring.map(coordsToStr).join(', ')})`).join(', ')})`)
            .join(', ')})`;

        case 'GeometryCollection':
          return `GEOMETRYCOLLECTION(${geometry.geometries
            .map(g => toWKT(g))
            .filter(g => g !== null)
            .join(', ')})`;

        default:
          return null;
      }
    };

    // Find the first feature with a valid geometry
    const featureWithGeometry = data.features.find(
      (feature): feature is Feature<Geometry> =>
        feature.type === 'Feature' && feature.geometry !== null
    );

    if (!featureWithGeometry) {
      return null;
    }

    return toWKT(featureWithGeometry.geometry);
  };

  /**
 * Converts a GeoJSON FeatureCollection (specifically looking for Polygons)
 * into a Well-Known Text (WKT) string representation of the first Polygon found.
 *
 * @param data The GeoJSON FeatureCollection from Mapbox Draw.
 * @returns A WKT string for the first Polygon, or null if no Polygons are found or input is null/empty.
 */
  static getParseFeatureData = (data: FeatureCollection | null): ParsedFeatureData | null => {
    if (!data || !data.features || data.features.length === 0) {
      return null;
    }
    const centroidFeature = turfCentroid(data);
    const calculatedArea = turfArea(data); // in sq metere
    return {
      boundary: this.parseStringFromFeature(data),
      centroid_wkt: this.parseStringFromFeature({
        type: 'FeatureCollection',
        features: [centroidFeature]
      }),
      area: this.convertSqMetersToHectares(calculatedArea)
    } as ParsedFeatureData;
  };

  /**
 * Converts an area from square meters to hectares and rounds it to two decimal places.
 *
 * @param {number} areaInSquareMeters The area in square meters.
 * @returns {number} The area in hectares, rounded to two decimal places.
 * @throws {Error} if areaInSquareMeters is not a number.
 */
  static convertSqMetersToHectares(areaInSquareMeters: number): number {
    if (typeof areaInSquareMeters !== 'number' || isNaN(areaInSquareMeters)) {
      throw new Error('Input must be a valid number for area in square meters.');
    }
    if (areaInSquareMeters < 0) {
      throw new Error('Area cannot be negative.');
    }

    const areaInHectares = convertArea(areaInSquareMeters, 'meters', 'hectares');
    return round(areaInHectares, 2);
  }

  /**
   * Creates a circular polygon as a WKT string around a center point.
   * @param {string} centerPointWkt - The center of the circle as a WKT string (e.g., 'POINT (10 20)').
   * @returns {string | null} A WKT string for the circular polygon, or null if creation fails.
   */
  /**
   * Creates a circular polygon and returns its data.
   * @param {string} centerPointWkt - The center of the circle as a WKT string (e.g., 'POINT (10 20)').
   * @returns {ParsedFeatureData} An object containing the boundary, area, and centroid. Returns default values on failure.
   */
  static createCircleAndGetData(centerPointWkt: string): ParsedFeatureData {
    try {
      const centerCoords = this.parsePointToArray(centerPointWkt);
      const center = point(centerCoords);
      const radius = 1000; // meters
      const options = {
        steps: 64,
        units: 'meters' as const,
      };
      const circleFeature = buffer(center, radius, options);

      if (!circleFeature) {
        console.error('Failed to create circle feature');
        return {
          boundary: '',
          area: 0,
          centroid_wkt: centerPointWkt,
        };
      }

      const featureCollection: FeatureCollection = {
        type: 'FeatureCollection',
        features: [circleFeature],
      };

      const boundary = this.parseStringFromFeature(featureCollection);
      if (!boundary) {
        console.error('Failed to parse circle feature to WKT');
        return {
          boundary: '',
          area: 0,
          centroid_wkt: centerPointWkt,
        };
      }

      const calculatedArea = turfArea(circleFeature);
      const area = this.convertSqMetersToHectares(calculatedArea);

      return {
        boundary,
        area,
        centroid_wkt: centerPointWkt,
      };
    } catch (error) {
      console.error('Error in createCircleAndGetData:', error);
      return {
        boundary: '',
        area: 0,
        centroid_wkt: centerPointWkt,
      };
    }
  }

  /**
   * Static utility function to parse a string in the format 'POINT (A B)' to an array [a, b].
   * @param {string} point - The string representation of the point (e.g., 'POINT (1 2)').
   * @returns {[number, number]} - The parsed coordinates as an array.
   */
  static parsePointToArray(point: string): [number, number] {
    const match = point.match(/POINT\s*\(([-\d.]+)\s+([-\d.]+)\)/);
    if (!match) {
      throw new Error('Invalid POINT format');
    }
    const [, x, y] = match;
    return [parseFloat(x), parseFloat(y)];
  }
}

export default MapboxFeature;